import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
// Import Font Awesome components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInstagram, 
  faLinkedinIn
} from '@fortawesome/free-brands-svg-icons';

// Styled Components
const FooterContainer = styled.footer`
  background: #EAE6E3;
  color: #333333;
  padding: 2rem 0 1rem;
  position: relative;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-family: 'Cormorant Garamond', serif;
    color: #32423B;
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1rem;
    position: relative;
    letter-spacing: -0.01em;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 40px;
      height: 2px;
      background: #BAB5B1;
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: 0.75rem;
    position: relative;
    padding-left: 12px;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 3px;
      height: 3px;
      background: #BAB5B1;
      border-radius: 50%;
    }
  }
  
  a {
    color: #666666;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    text-decoration: none;
    
    &:hover {
      color: #3F534C;
    }
  }
  
  p {
    color: #666666;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    line-height: 1.6;
  }
  
  .contact-info {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
    color: #666666;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    
    span:first-child {
      margin-right: 0.75rem;
      color: #3F534C;
      font-size: 1rem;
    }
  }
`;

const LogoColumn = styled(FooterColumn)`
  img {
    height: 200px;
    width: auto;
    margin-bottom: 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #FFFFFF;
    border: 1px solid #BAB5B1;
    border-radius: 50%;
    transition: all 0.3s ease;
    color: #666666;
    
    &:hover {
      background: #3F534C;
      border-color: #3F534C;
      color: #FFFFFF;
      transform: translateY(-2px);
    }
    
    .icon {
      font-size: 0.9rem;
    }
  }
`;

const Copyright = styled.div`
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 1rem 2rem 0;
  border-top: 1px solid #BAB5B1;
  text-align: center;
  
  p {
    color: #666666;
    font-family: 'Inter', sans-serif;
    font-size: 0.85rem;
    
    a {
      color: #3F534C;
      transition: all 0.3s ease;
      text-decoration: none;
      
      &:hover {
        color: #32423B;
      }
    }
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <LogoColumn>
          <img src="/logo/Logo-04.png" alt="Veridicus Logo" />
        </LogoColumn>
        
        <FooterColumn>
          <h3>Serviços</h3>
          <ul>
            <li><Link to="/servicos/gestao-cadastros">Abertura de Empresas</Link></li>
            <li><Link to="/servicos/documentos">Atos e Livros Societários</Link></li>
            <li><Link to="/servicos/backoffice">Certidões e Documentos</Link></li>
            <li><Link to="/servicos/consultoria">Consultoria empresarial</Link></li>
            <li><Link to="/servicos/registro-marca">Propriedade Intelectual</Link></li>
          </ul>
        </FooterColumn>
        
        <FooterColumn>
          <h3>Links Úteis</h3>
          <ul>
            <li><Link to="/about-us">Sobre Nós</Link></li>
            <li><Link to="/blog">Blog</Link></li>
            <li><Link to="/faq">Perguntas Frequentes</Link></li>
            <li><Link to="/termos">Termos de Uso</Link></li>
            <li><Link to="/privacidade">Política de Privacidade</Link></li>
          </ul>
        </FooterColumn>
        
        <FooterColumn>
          <h3>Contato</h3>
          <div className="contact-info">
            <span>📍</span>
            <span>Rua Paulistânia, 381, 1º andar, conjunto 11 - São Paulo, SP - CEP: 05440-000</span>
          </div>
          <div className="contact-info">
            <span>📞</span>
            <span>(11) 5128-0555</span>
          </div>
          <div className="contact-info">
            <span>✉️</span>
            <span>comercial@veridicus.com.br</span>
          </div>
          <SocialLinks>
            <a href="https://www.instagram.com/veridicusbr?igsh=MWlpbXBzZDlsNmQ4Nw==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FontAwesomeIcon icon={faInstagram} className="icon" />
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FontAwesomeIcon icon={faLinkedinIn} className="icon" />
            </a>
          </SocialLinks>
        </FooterColumn>
      </FooterContent>
      
      <Copyright>
        <p>
          © {new Date().getFullYear()} Veridicus - Registros Comerciais. Todos os direitos reservados.
          Desenvolvido por <a href="https://devteamsix.com.br" target="_blank" rel="noopener noreferrer">Dev TeamSix</a>        
        </p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;